import {
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AlertBar from "./components/modals/AlertBar";
import { useDispatch, useSelector } from "react-redux";
import Footer from "./components/Footer";
import SubmitEmail from "./components/SubmitEmail";
import { useEffect, useState } from "react";
import { fetchSubscription } from "./utils/fetchSubscription";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db, auth } from "./authentication/firebase";
import { userActions } from "./store/userSlice";
import AuthLoader from "./components/AuthLoader";
import { getRedirectResult, onAuthStateChanged } from "firebase/auth";
import ProductAdPopup from "./components/modals/ProductAdPopup";
import RedeemOfferPopup from "./components/modals/RedeemOfferPopup";
import { collection, getDocs, query, where } from "firebase/firestore";

export default function RootLayout() {
  const alert = useSelector((state) => state.alert);
  const [redirectLoading, setRedirectLoading] = useState(true);
  const [showProductAdPopup, setShowProductAdPopup] = useState(false);
  const [showRedeemOffer, setShowRedeemOffer] = useState(false);
  const [redeemOfferData, setRedeemOfferData] = useState(null);
  const [productAdTimer, setProductAdTimer] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = auth.currentUser

  // Function to check if user should see product ad popup (existing users)
  const shouldShowProductAdPopup = () => {
    const hasSeenProductAdInSession = sessionStorage.getItem("productAdPopupShown");
    // Show popup if: 1) not seen in this session AND 2) user is authenticated
    return currentUser && hasSeenProductAdInSession === null ;
  };

  // Function to check if user should see redeem offer popup after login
  const shouldShowRedeemOffer = () => {
    const hasSeenRedeemOffer = sessionStorage.getItem("redeemOfferShown");
    
    // Show redeem offer if: 1) user is authenticated AND 2) haven't seen redeem offer yet
    return currentUser && hasSeenRedeemOffer !== "true";
  };

  // Function to fetch popup configuration
  const fetchPopupConfig = async (type) => {
    try {
      const popupsCollectionRef = collection(db, "popups");
      const popupQuery = query(popupsCollectionRef, where("type", "==", type));
      const popupSnapshot = await getDocs(popupQuery);
      
      if (!popupSnapshot.empty) {
        const popupData = popupSnapshot.docs[0].data();
        return popupData;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching ${type} popup config:`, error);
      return null;
    }
  };


  // Function to setup product ad popup timer
  const setupProductAdPopupTimer = async () => {
    console.log("shouldShowProductAdPopup", shouldShowProductAdPopup());
    if (shouldShowProductAdPopup()) {
      const popupConfig = await fetchPopupConfig("product");
      if (popupConfig && popupConfig.popupTime) {
        const timer = setTimeout(() => {
          setShowProductAdPopup(true);
        }, popupConfig.popupTime * 1000); // Convert seconds to milliseconds
        
        setProductAdTimer(timer);
      }
    }
  };

  // Function to setup redeem offer popup timer
  const setupRedeemOfferTimer = async () => {
    if (shouldShowRedeemOffer()) {
      const popupConfig = await fetchPopupConfig("new-user");
      if (popupConfig) {
        setRedeemOfferData(popupConfig);
        // Show redeem offer popup immediately after login
        setShowRedeemOffer(true);
      }
    }
  };


  // Handle product ad popup close
  const handleCloseProductAdPopup = () => {
    setShowProductAdPopup(false);
    sessionStorage.setItem("productAdPopupShown", "true");
    
    // Clear timer if it exists
    if (productAdTimer) {
      clearTimeout(productAdTimer);
      setProductAdTimer(null);
    }
  };

  // Handle redeem offer popup close
  const handleCloseRedeemOffer = () => {
    setShowRedeemOffer(false);
    // Don't mark as seen when just closing - only when successfully redeemed
    // sessionStorage.setItem("redeemOfferShown", "true");
  };

  // Handle successful offer redemption
  const handleSuccessfulRedeem = () => {
    // Only mark as seen when user successfully redeems the offer
    sessionStorage.setItem("redeemOfferShown", "true");
  };

  async function fetchUserInfo(uid) {
    try {
      const userInfo = await getDoc(doc(db, "userInfo", uid));
      return userInfo.data();
    } catch (e) {
      console.error("Error fetching user info:", e);
    }
  }

  async function setUserInfo(currentUser) {
    const subscription = await fetchSubscription(currentUser.uid);
    const userInfo = await fetchUserInfo(currentUser.uid);
    const localUserInfo = JSON.parse(localStorage.getItem("userInfo"));

    if (!userInfo && localUserInfo) {
      const newUserInfo = {
        uid: currentUser.uid,
        email: currentUser.email,
        createdAt: serverTimestamp(),
        ...localUserInfo,
      };
      dispatch(
        userActions.setCurrentUser({
          ...currentUser,
          subscription,
          userInfo: newUserInfo,
        })
      );

      try {
        await setDoc(doc(db, "userInfo", currentUser.uid), newUserInfo);
        localStorage.removeItem("userInfo");
      } catch (error) {
        console.error("Error saving user info:", error);
      }
    } else {
      dispatch(
        userActions.setCurrentUser({
          ...currentUser,
          subscription,
          userInfo,
        })
      );
    }
  }

  async function redirectURLHandler() {
    try {
      const result = await getRedirectResult(auth);
      
      // Use current path as the fallback instead of root
      const currentPath = window.location.pathname;
      let { from } = location.state || { from: "/" };
      
      if (result?.user) {
        setUserInfo(result.user);
        // Only navigate if from is different from current path
        if (from !== currentPath) {
          navigate(from);
        }
      } else {
        // Add this fallback
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          if (user) {
            setUserInfo(user);
            
            // Only navigate if location state exists and path is different
            if (location.state && from !== currentPath) {
              navigate(from);
            }
            
            // Otherwise stay on the current page
          }
          unsubscribe(); // Clean up listener after first use
        });
      }
    } catch (error) {
      console.error("Error handling redirect:", error);
    } finally {
      setRedirectLoading(false);
    }
  }

  useEffect(() => {
    redirectURLHandler();
  }, []);

  // Setup popup timers when component mounts and auth state is resolved
  useEffect(() => {
    const setupPopups = async () => {
      if (!redirectLoading) {
        setupProductAdPopupTimer();
        await setupRedeemOfferTimer();
      }
    };
    
    setupPopups();
    
    // Cleanup timers on unmount
    return () => {
      if (productAdTimer) {
        clearTimeout(productAdTimer);
      }
    };
  }, [redirectLoading, currentUser]);

  if (redirectLoading) {
    return <AuthLoader />;
  } else
    return (
      <div className="App" id="app">
        <AlertBar alertStates={alert} />
        <ProductAdPopup isOpen={showProductAdPopup} onClose={handleCloseProductAdPopup} />
        <RedeemOfferPopup 
          isOpen={showRedeemOffer} 
          onClose={handleCloseRedeemOffer} 
          onSuccessfulRedeem={handleSuccessfulRedeem}
          popupData={redeemOfferData}
          couponCode={redeemOfferData?.couponCode}
        />
        <Outlet />
        {window.location.pathname !== "/signup" &&
          window.location.pathname !== "/login" &&
          window.location.pathname !== "/" && (
            <>
              {/* <SubmitEmail /> */}
              <Footer />
            </>
          )}
      </div>
    );
}
